/**
 * Where the user guide is, and how to open it at one section.
 *
 * The guide is a single static page shipped inside the build:
 * `scripts/copy-guide.mjs` copies `docs/user-guide/index.html` into `public/`
 * at prebuild, so Vite lands it at `/guide/index.html` in `dist/` and, for a
 * brand, in `dist/<key>/`. Nothing is fetched from another host.
 *
 * **`/guide/index.html`, not `/guide/`.** The Caddyfile's `try_files {path}
 * /index.html` falls back to the SPA shell for any path that is not a file, so
 * a directory URL would serve the app again rather than the guide — silently,
 * and only in production.
 *
 * ## Sections are anchors, and they are checked by hand
 *
 * The ids below exist in the guide today. They are a plain union rather than a
 * free string so a rename shows up here as a type error instead of as a link
 * that scrolls nowhere — the guide is a separate file the compiler cannot see.
 */

/** The shipped guide. */
export const GUIDE_URL = "/guide/index.html";

/**
 * A section of the guide that can be linked to directly.
 *
 * Each is an `id` in `docs/user-guide/index.html`. Add one here only after
 * adding it there.
 */
export type GuideSection =
  | "start"
  | "map"
  | "layouts"
  | "layout-panel"
  | "templates"
  | "chart-templates"
  | "layout-templates"
  | "charts"
  | "symbol"
  | "interval"
  | "chart-type"
  | "panes"
  | "history"
  | "indicators"
  | "screener"
  | "screener-groups"
  | "track-lists"
  | "columns"
  | "filters"
  | "saved-filters"
  | "computed"
  | "themes"
  | "saved"
  | "keys";

/** The guide's URL, at a section when one is named. */
export function guideHref(section?: GuideSection): string {
  return section ? `${GUIDE_URL}#${section}` : GUIDE_URL;
}

/**
 * Open the guide in a new tab.
 *
 * `noopener` because the guide has no business reaching back into the app, and
 * because without it the new tab shares this one's process.
 *
 * Returns whether the window actually opened: some browsers refuse
 * `window.open` outright, and a caller that renders its own fallback needs to
 * know rather than leaving the reader looking at nothing.
 */
export function openGuide(section?: GuideSection): boolean {
  const opened = window.open(guideHref(section), "_blank", "noopener,noreferrer");
  return Boolean(opened);
}

/**
 * Open the guide at an anchor read **from the guide itself** — the command
 * search's Guide rows (`lib/guide-index.ts`). A free string here, unlike
 * `GuideSection`, because the anchor came out of the shipped page, so it exists
 * by construction; the union is for links written by hand.
 */
export function openGuideAt(anchor: string): void {
  window.open(`${GUIDE_URL}#${encodeURIComponent(anchor)}`, "_blank", "noopener,noreferrer");
}
